import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Keyboard } from "lucide-react";

export interface ShortcutDefinition {
  keys: string[];
  description: string;
  category: string;
}

interface KeyboardShortcutsHelpProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shortcuts?: ShortcutDefinition[];
}

const defaultShortcuts: ShortcutDefinition[] = [
  {
    keys: ["Ctrl/Cmd", "N"],
    description: "Create a new note",
    category: "Global",
  },
  {
    keys: ["Ctrl/Cmd", "K"],
    description: "Open search",
    category: "Global",
  },
];

const groupShortcuts = (list: ShortcutDefinition[]) =>
  list.reduce((acc, shortcut) => {
    if (!acc[shortcut.category]) {
      acc[shortcut.category] = [] as ShortcutDefinition[];
    }
    acc[shortcut.category].push(shortcut);
    return acc;
  }, {} as Record<string, ShortcutDefinition[]>);

export default function KeyboardShortcutsHelp({
  open,
  onOpenChange,
  shortcuts = defaultShortcuts,
}: KeyboardShortcutsHelpProps) {
  const groupedShortcuts = groupShortcuts(shortcuts);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Keyboard className="h-5 w-5" />
            Keyboard Shortcuts
          </DialogTitle>
          <DialogDescription>
            Use these keyboard shortcuts to move faster through MeetHub
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {Object.entries(groupedShortcuts).map(([category, shortcuts]) => (
            <div key={category}>
              <h3 className="text-sm font-semibold mb-3 text-muted-foreground">
                {category}
              </h3>
              <div className="space-y-2">
                {shortcuts.map((shortcut) => (
                  <Card key={`${shortcut.category}-${shortcut.description}`} className="border-border/50">
                    <CardContent className="p-3 flex items-center justify-between">
                      <span className="text-sm">{shortcut.description}</span>
                      <div className="flex items-center gap-2">
                        {shortcut.keys.map((key) => (
                          <kbd
                            key={`${shortcut.description}-${key}`}
                            className="px-3 py-1.5 text-sm font-semibold bg-muted border border-border rounded-md shadow-sm"
                          >
                            {key}
                          </kbd>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 p-4 bg-muted/50 rounded-lg">
          <p className="text-xs text-muted-foreground">
            💡 <strong>Tip:</strong> These shortcuts won't work when you're typing in a text field or chat box.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
